import { Injectable } from "@angular/core";
import {
  BackgroundBlurStrength,
  BackgroundBlurVideoFrameProcessor,
  ConsoleLogger,
  DataMessage,
  DefaultActiveSpeakerPolicy,
  DefaultBrowserBehavior,
  DefaultDeviceController,
  DefaultMeetingSession,
  DefaultVideoTransformDevice,
  DeviceChangeObserver,
  LogLevel,
  MeetingSessionConfiguration,
  MeetingSessionStatus,
  MeetingSessionStatusCode,
  RemovableAnalyserNode,
  VideoFrameProcessor,
  VideoInputDevice,
  VolumeIndicatorCallback,
} from "amazon-chime-sdk-js";
import { SdkTranscriptFrame } from "amazon-chime-sdk-js/build/signalingprotocol/SignalingProtocol";

export interface TranscriptUpdate {
  resultId: string;
  partial: boolean;
  speakerId: string;
  text: string;
  startTimeMs: number;
  failed?: boolean;
  statusMessage?: string;
}

export interface PresenceUpdate {
  attendeeId: string;
  externalUserId: string;
  present: boolean;
}

/** Choices made on the pre-join screen and applied when the session starts. */
export interface JoinOptions {
  muted?: boolean;
  cameraOn?: boolean;
  noAudio?: boolean;
  audioInputId?: string;
  audioOutputId?: string;
  videoInputId?: string;
  blur?: boolean;
  /** Runs once the session exists but before it connects, so no early data message is missed. */
  beforeStart?: () => void;
}

/** Which local devices actually started; a failed device does not stop the meeting. */
export interface JoinResult {
  audio: boolean;
  video: boolean;
  /** leave() ran while the join was still opening devices: nothing was started and all was released. */
  cancelled?: boolean;
}

export interface DeviceLists {
  mics: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  cameras: MediaDeviceInfo[];
}

/** What happened to one preview device: off (not asked for), on, or why it could not open. */
export type PreviewDeviceState = "off" | "on" | "blocked" | "missing" | "busy" | "failed";

export interface PreviewResult {
  /** The microphone's analyser for the level meter; null when the microphone is not on. */
  analyser: AnalyserNode | null;
  mic: PreviewDeviceState;
  camera: PreviewDeviceState;
}

export interface PreviewOptions {
  cameraOn: boolean;
  noAudio: boolean;
  audioInputId?: string;
  videoInputId?: string;
  blur?: boolean;
}

export interface MeetingMessage {
  senderAttendeeId: string;
  senderExternalUserId: string;
  timestampMs: number;
  data: any;
}

export type StopReason = "left" | "ended" | "other-device" | "removed" | "error";

/** The camera pipeline: a raw device id, or the same device wrapped in a blur transform. */
interface CameraPipe {
  processor: VideoFrameProcessor | null;
  transform: DefaultVideoTransformDevice | null;
}

interface PreviewState {
  controller: DefaultDeviceController;
  video: HTMLVideoElement;
  pipe: CameraPipe;
  analyser: RemovableAnalyserNode | null;
}

@Injectable({ providedIn: "root" })
export class ChimeMeetingService {
  private session: DefaultMeetingSession | null = null;
  private devices: DefaultDeviceController | null = null;
  private logger = new ConsoleLogger("PortalMeeting", LogLevel.WARN);
  private audioOnly = false;
  /** The last join opened a microphone (the phone call panel warns when it did not). */
  microphoneReady = false;
  private presenceHandler: ((attendeeId: string, present: boolean, externalUserId?: string) => void) | null = null;
  private topics = new Set<string>();
  private volumeHandlers = new Map<string, VolumeIndicatorCallback>();
  private speakerHandler: ((attendeeIds: string[]) => void) | null = null;
  private muteHandler: ((muted: boolean) => void) | null = null;
  private deviceObserver: DeviceChangeObserver | null = null;
  private camera: CameraPipe = { processor: null, transform: null };
  private cameraId = "";
  private micId = "";
  private speakerId = "";
  private blurOn = false;
  private blurCheck: Promise<boolean> | null = null;
  private preview: PreviewState | null = null;
  /** Bumped by every leave(): a join that sees it change was overtaken and backs out. */
  private generation = 0;

  localAttendeeId(): string {
    return (this.session as DefaultMeetingSession | null)?.configuration?.credentials?.attendeeId || "";
  }

  localExternalUserId(): string {
    const credentials = (this.session as DefaultMeetingSession | null)?.configuration?.credentials;
    return credentials?.externalUserId || "";
  }

  /** Whether this browser can run a Chime audio session (WebRTC + microphone capture). */
  isBrowserSupported(): boolean {
    try {
      return new DefaultBrowserBehavior().isSupported() && !!navigator.mediaDevices?.getUserMedia;
    } catch {
      return false;
    }
  }

  async join(
    meetingResponse: any,
    attendeeResponse: any,
    audioElement: HTMLAudioElement,
    observer: any,
    audioOnly = false,
    onTranscript?: (line: TranscriptUpdate) => void,
    onPresence?: (update: PresenceUpdate) => void,
    opts: JoinOptions = {}
  ): Promise<JoinResult> {
    await this.leave();
    // Every await below can be overtaken by leave() (Leave during "Connecting…", Cancel on a
    // call, navigating away). The join then releases what it opened and never calls start(),
    // because once this.session is cleared nothing could stop that session again.
    const generation = this.generation;
    const pipe = this.camera;
    this.audioOnly = audioOnly;
    const deviceController = new DefaultDeviceController(this.logger);
    const configuration = new MeetingSessionConfiguration(meetingResponse, attendeeResponse);
    this.devices = deviceController;
    this.session = new DefaultMeetingSession(configuration, this.logger, deviceController);
    const audioVideo = this.session.audioVideo;
    const overtaken = () => generation !== this.generation;
    const backOut = async (): Promise<JoinResult> => {
      try { await audioVideo.stopVideoInput(); } catch { /* no camera */ }
      try { await audioVideo.stopAudioInput(); } catch { /* no microphone */ }
      await this.disposeCamera(pipe);
      this.microphoneReady = false;
      return { audio: false, video: false, cancelled: true };
    };
    audioVideo.addObserver(observer);
    audioVideo.addContentShareObserver(observer);
    if (onPresence) {
      this.presenceHandler = (attendeeId: string, present: boolean, externalUserId?: string) => {
        if (!attendeeId || attendeeId.endsWith("#content")) return;
        onPresence({ attendeeId, externalUserId: externalUserId || "", present });
      };
      audioVideo.realtimeSubscribeToAttendeeIdPresence(this.presenceHandler);
    }
    if (onTranscript) {
      audioVideo.realtimeSubscribeToReceiveDataMessage("aws:chime:transcription", (message) => {
        this.readTranscript(message?.data, onTranscript);
      });
    }
    await audioVideo.bindAudioElement(audioElement);
    if (overtaken()) return backOut();
    // Remote audio has to play even where autoplay is strict: play again once the session starts.
    // "Don't use audio" keeps the element silent (another device in the room carries the sound).
    audioElement.autoplay = true;
    audioElement.muted = !!opts.noAudio;
    audioElement.volume = 1;
    const playMeetingAudio = () => {
      if (!opts.noAudio) void audioElement.play().catch(() => undefined);
    };
    if (observer && !opts.noAudio) {
      const priorDidStart = observer.audioVideoDidStart;
      observer.audioVideoDidStart = () => {
        playMeetingAudio();
        if (typeof priorDidStart === "function") priorDidStart();
      };
    }

    this.microphoneReady = false;
    if (!opts.noAudio) {
      try {
        // A permission prompt left open must not hold the join forever.
        const audioInputs = await Promise.race([
          audioVideo.listAudioInputDevices(),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error("microphone timeout")), 5000)),
        ]);
        this.micId = this.pick(audioInputs, opts.audioInputId);
        if (this.micId) {
          await audioVideo.startAudioInput(this.micId);
          this.microphoneReady = true;
        }
      } catch {
        // Remote audio still plays without a microphone. Callers read `microphoneReady` / the
        // result's `audio`: the call panel warns, the meeting room joins muted.
        this.micId = "";
      }
    }
    if (overtaken()) return backOut();
    // Muting before start() means others never hear a moment of audio from someone who joined muted.
    if (opts.muted || opts.noAudio || !this.micId) audioVideo.realtimeMuteLocalAudio();
    if (opts.audioOutputId && this.canChooseSpeaker()) {
      this.speakerId = opts.audioOutputId;
      await audioVideo.chooseAudioOutput(opts.audioOutputId).catch(() => undefined);
    }

    if (!audioOnly) {
      this.blurOn = !!opts.blur;
      const start = (device: VideoInputDevice) => audioVideo.startVideoInput(device);
      try {
        const videoInputs = await audioVideo.listVideoInputDevices();
        this.cameraId = this.pick(videoInputs, opts.videoInputId);
        if (this.cameraId && !overtaken()) await this.applyCamera(pipe, start, this.cameraId, this.blurOn);
      } catch {
        // If the blur pipeline is what failed, fall back to the plain camera.
        const retry = !!this.cameraId && this.blurOn;
        this.blurOn = false;
        try {
          if (retry && !overtaken()) await this.applyCamera(pipe, start, this.cameraId, false);
          else this.cameraId = "";
        } catch {
          this.cameraId = "";
        }
      }
    }
    if (overtaken()) return backOut();
    opts.beforeStart?.();
    audioVideo.start();
    if (audioOnly) playMeetingAudio();
    // Starting the tile only when the camera is on keeps a camera-off join from flashing video to others.
    if (this.cameraId && opts.cameraOn !== false) {
      audioVideo.startLocalVideoTile();
    }
    return { audio: !!this.micId, video: !!this.cameraId };
  }

  hasCamera(): boolean {
    return !!this.cameraId;
  }

  async toggleMute(muted: boolean) {
    if (!this.session) return;
    if (muted) this.session.audioVideo.realtimeMuteLocalAudio();
    else this.session.audioVideo.realtimeUnmuteLocalAudio();
  }

  hasMic(): boolean {
    return !!this.micId;
  }

  async toggleCamera(enabled: boolean) {
    if (!this.session || this.audioOnly) return;
    if (enabled) this.session.audioVideo.startLocalVideoTile();
    else this.session.audioVideo.stopLocalVideoTile();
  }

  async toggleScreenShare(enabled: boolean) {
    if (!this.session || this.audioOnly) return;
    if (enabled) await this.session.audioVideo.startContentShareFromScreenCapture(undefined, 15);
    else await this.session.audioVideo.stopContentShare();
  }

  canShareScreen(): boolean {
    return !!navigator.mediaDevices && typeof (navigator.mediaDevices as any).getDisplayMedia === "function";
  }

  bindTile(tileId: number, element: HTMLVideoElement) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo) return;
    const tile = audioVideo.getVideoTile(tileId);
    if (tile && tile.state().boundVideoElement === element) return;
    audioVideo.bindVideoElement(tileId, element);
  }

  /** Maps why the session stopped to the reason shown on the left-meeting screen. */
  stopReason(status: MeetingSessionStatus | null | undefined): StopReason {
    switch (status?.statusCode()) {
      case MeetingSessionStatusCode.Left:
        return "left";
      case MeetingSessionStatusCode.MeetingEnded:
        return "ended";
      case MeetingSessionStatusCode.AudioJoinedFromAnotherDevice:
        return "other-device";
      case MeetingSessionStatusCode.AudioAttendeeRemoved:
        return "removed";
      default:
        return "error";
    }
  }

  // ---- Devices ----

  async listDevices(): Promise<DeviceLists> {
    const controller = this.session?.audioVideo || this.preview?.controller;
    if (!controller) return { mics: [], speakers: [], cameras: [] };
    const [mics, speakers, cameras] = await Promise.all([
      controller.listAudioInputDevices(),
      controller.listAudioOutputDevices(),
      controller.listVideoInputDevices(),
    ]);
    return { mics, speakers, cameras };
  }

  currentDevices(): { audioInputId: string; audioOutputId: string; videoInputId: string; blur: boolean } {
    return { audioInputId: this.micId, audioOutputId: this.speakerId, videoInputId: this.cameraId, blur: this.blurOn };
  }

  canChooseSpeaker(): boolean {
    return typeof HTMLMediaElement !== "undefined" && "setSinkId" in HTMLMediaElement.prototype;
  }

  async chooseMic(deviceId: string) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo || !deviceId) return;
    const muted = audioVideo.realtimeIsLocalAudioMuted();
    await audioVideo.startAudioInput(deviceId);
    this.micId = deviceId;
    // A freshly opened microphone starts enabled, so carry the mute across the switch.
    if (muted) this.devices?.muteLocalAudioInputStream();
  }

  async chooseSpeaker(deviceId: string) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo || !this.canChooseSpeaker()) return;
    await audioVideo.chooseAudioOutput(deviceId || null);
    this.speakerId = deviceId;
  }

  async chooseCamera(deviceId: string) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo || this.audioOnly || !deviceId) return;
    await this.applyCamera(this.camera, (device) => audioVideo.startVideoInput(device), deviceId, this.blurOn);
    this.cameraId = deviceId;
  }

  async setBlur(on: boolean) {
    const audioVideo = this.session?.audioVideo;
    this.blurOn = on;
    if (!audioVideo || this.audioOnly || !this.cameraId) return;
    await this.applyCamera(this.camera, (device) => audioVideo.startVideoInput(device), this.cameraId, on);
  }

  blurSupported(): Promise<boolean> {
    if (!this.blurCheck) {
      this.blurCheck = BackgroundBlurVideoFrameProcessor.isSupported(undefined, { logger: this.logger }).catch(() => false);
    }
    return this.blurCheck;
  }

  onDevicesChanged(callback: () => void) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo) return;
    if (this.deviceObserver) audioVideo.removeDeviceChangeObserver(this.deviceObserver);
    this.deviceObserver = {
      audioInputsChanged: () => callback(),
      audioOutputsChanged: () => callback(),
      videoInputsChanged: () => callback(),
    };
    audioVideo.addDeviceChangeObserver(this.deviceObserver);
  }

  // ---- Pre-join preview (no meeting session yet) ----

  /**
   * Starts the camera and microphone preview. Each device is tried on its own, so a camera that
   * fails still leaves the microphone (and its level meter) working, and the result says what
   * happened to each so the lobby can explain it.
   */
  async startPreview(video: HTMLVideoElement, opts: PreviewOptions): Promise<PreviewResult> {
    await this.stopPreview();
    const controller = new DefaultDeviceController(this.logger);
    const preview: PreviewState = { controller, video, pipe: { processor: null, transform: null }, analyser: null };
    this.preview = preview;
    const result: PreviewResult = { analyser: null, mic: "off", camera: "off" };
    try {
      if (!opts.noAudio) {
        try {
          const mics = await controller.listAudioInputDevices();
          const micId = this.pick(mics, opts.audioInputId);
          if (micId) {
            await controller.startAudioInput(micId);
            preview.analyser = controller.createAnalyserNodeForAudioInput();
            result.mic = "on";
          } else {
            result.mic = this.missingOrBlocked(mics);
          }
        } catch (err) {
          result.mic = this.deviceFailure(err);
        }
      }
      if (opts.cameraOn && this.preview === preview) {
        try {
          const cameras = await controller.listVideoInputDevices();
          const cameraId = this.pick(cameras, opts.videoInputId);
          if (cameraId) {
            await this.applyCamera(preview.pipe, (device) => controller.startVideoInput(device), cameraId, !!opts.blur);
            if (this.preview === preview) controller.startVideoPreviewForVideoInput(video);
            result.camera = "on";
          } else {
            result.camera = this.missingOrBlocked(cameras);
          }
        } catch (err) {
          result.camera = this.deviceFailure(err);
        }
      }
    } finally {
      // stopPreview() may have run while a device was opening; release what this attempt opened.
      if (this.preview !== preview) await this.releasePreview(preview);
    }
    result.analyser = this.preview === preview ? preview.analyser : null;
    return result;
  }

  /** No usable device id: none plugged in, or (ids and labels blank) permission was refused. */
  private missingOrBlocked(devices: MediaDeviceInfo[]): PreviewDeviceState {
    return devices.length ? "blocked" : "missing";
  }

  /** Why opening a device failed, from the browser's (or the Chime SDK's) error name. */
  private deviceFailure(err: unknown): PreviewDeviceState {
    const name = String((err as { name?: string })?.name || "") + " " + String((err as { message?: string })?.message || "");
    if (/NotAllowed|PermissionDenied|Security|Permission/i.test(name)) return "blocked";
    if (/NotReadable|TrackStart|Abort|in use/i.test(name)) return "busy";
    if (/NotFound|DevicesNotFound|Overconstrained/i.test(name)) return "missing";
    return "failed";
  }

  async stopPreview() {
    const preview = this.preview;
    this.preview = null;
    if (preview) await this.releasePreview(preview);
  }

  private async releasePreview(preview: PreviewState) {
    try { preview.analyser?.removeOriginalInputs(); } catch { /* already removed */ }
    preview.analyser = null;
    try { preview.controller.stopVideoPreviewForVideoInput(preview.video); } catch { /* not previewing */ }
    try { await preview.controller.stopVideoInput(); } catch { /* no camera */ }
    try { await preview.controller.stopAudioInput(); } catch { /* no microphone */ }
    await this.disposeCamera(preview.pipe);
    try { await preview.controller.destroy(); } catch { /* already destroyed */ }
  }

  // ---- Realtime data messages, volume and active speaker ----

  sendMessage(topic: string, data: any, lifetimeMs = 0): boolean {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo) return false;
    audioVideo.realtimeSendDataMessage(topic, data, lifetimeMs);
    return true;
  }

  onMessage(topic: string, callback: (message: MeetingMessage) => void) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo) return;
    this.topics.add(topic);
    audioVideo.realtimeSubscribeToReceiveDataMessage(topic, (message: DataMessage) => {
      if (message.throttled) return;
      let data: any;
      try {
        data = message.json();
      } catch {
        return;
      }
      callback({
        senderAttendeeId: message.senderAttendeeId,
        senderExternalUserId: message.senderExternalUserId,
        timestampMs: message.timestampMs,
        data,
      });
    });
  }

  watchVolume(attendeeId: string, callback: VolumeIndicatorCallback) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo || !attendeeId || this.volumeHandlers.has(attendeeId)) return;
    this.volumeHandlers.set(attendeeId, callback);
    audioVideo.realtimeSubscribeToVolumeIndicator(attendeeId, callback);
  }

  unwatchVolume(attendeeId: string) {
    const callback = this.volumeHandlers.get(attendeeId);
    if (!callback) return;
    this.volumeHandlers.delete(attendeeId);
    try { this.session?.audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId, callback); } catch { /* session stopped */ }
  }

  onActiveSpeaker(callback: (attendeeIds: string[]) => void) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo) return;
    this.speakerHandler = callback;
    audioVideo.subscribeToActiveSpeakerDetector(new DefaultActiveSpeakerPolicy(), callback);
  }

  onLocalMuteChanged(callback: (muted: boolean) => void) {
    const audioVideo = this.session?.audioVideo;
    if (!audioVideo) return;
    this.muteHandler = callback;
    audioVideo.realtimeSubscribeToMuteAndUnmuteLocalAudio(callback);
  }

  async leave() {
    // Everything this session owned is detached before the first await, so a join() that starts
    // while this leave is still stopping gets fresh state and is never reset by it afterwards.
    this.generation++;
    const session = this.session;
    const camera = this.camera;
    this.session = null;
    this.devices = null;
    this.camera = { processor: null, transform: null };
    this.cameraId = "";
    this.micId = "";
    this.speakerId = "";
    this.blurOn = false;
    if (!session) {
      await this.disposeCamera(camera);
      return;
    }
    const audioVideo = session.audioVideo;
    try { audioVideo.stopLocalVideoTile(); } catch { /* already stopped */ }
    try { audioVideo.stopContentShare(); } catch { /* not sharing */ }
    try { audioVideo.realtimeUnsubscribeFromReceiveDataMessage("aws:chime:transcription"); } catch { /* not subscribed */ }
    for (const topic of this.topics) {
      try { audioVideo.realtimeUnsubscribeFromReceiveDataMessage(topic); } catch { /* not subscribed */ }
    }
    this.topics.clear();
    for (const [attendeeId, callback] of this.volumeHandlers) {
      try { audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId, callback); } catch { /* not subscribed */ }
    }
    this.volumeHandlers.clear();
    if (this.speakerHandler) {
      try { audioVideo.unsubscribeFromActiveSpeakerDetector(this.speakerHandler); } catch { /* not subscribed */ }
      this.speakerHandler = null;
    }
    if (this.muteHandler) {
      try { audioVideo.realtimeUnsubscribeToMuteAndUnmuteLocalAudio(this.muteHandler); } catch { /* not subscribed */ }
      this.muteHandler = null;
    }
    if (this.deviceObserver) {
      try { audioVideo.removeDeviceChangeObserver(this.deviceObserver); } catch { /* not observing */ }
      this.deviceObserver = null;
    }
    if (this.presenceHandler) {
      try { audioVideo.realtimeUnsubscribeToAttendeeIdPresence(this.presenceHandler); } catch { /* not subscribed */ }
      this.presenceHandler = null;
    }
    try {
      const stop = audioVideo.stop();
      await Promise.race([stop, new Promise((resolve) => setTimeout(resolve, 1500))]);
    } catch {
      // The session is already stopping.
    }
    try { await audioVideo.stopVideoInput(); } catch { /* no camera */ }
    try { await audioVideo.stopAudioInput(); } catch { /* no microphone */ }
    await this.disposeCamera(camera);
  }

  /**
   * Switches the camera, wrapping it in a blur transform when asked. The transform's
   * processors are shared across camera switches and only destroyed when blur turns off.
   */
  private async applyCamera(
    pipe: CameraPipe,
    start: (device: VideoInputDevice) => Promise<unknown>,
    deviceId: string,
    blur: boolean
  ) {
    const previous = pipe.transform;
    let next: VideoInputDevice = deviceId;
    if (blur && (await this.blurSupported())) {
      if (!pipe.processor) {
        pipe.processor = (await BackgroundBlurVideoFrameProcessor.create(undefined, {
          blurStrength: BackgroundBlurStrength.MEDIUM,
          logger: this.logger,
        })) || null;
      }
      if (pipe.processor) {
        next = previous ? previous.chooseNewInnerDevice(deviceId) : new DefaultVideoTransformDevice(this.logger, deviceId, [pipe.processor]);
      }
    }
    await start(next);
    pipe.transform = next instanceof DefaultVideoTransformDevice ? next : null;
    if (previous && !pipe.transform) {
      await previous.stop();
      pipe.processor = null;
    }
  }

  private async disposeCamera(pipe: CameraPipe) {
    const transform = pipe.transform;
    const processor = pipe.processor;
    pipe.transform = null;
    pipe.processor = null;
    try {
      if (transform) await transform.stop();
      else if (processor) await processor.destroy();
    } catch {
      // The pipeline is already torn down.
    }
  }

  private pick(devices: MediaDeviceInfo[], preferred?: string): string {
    if (preferred && devices.some((device) => device.deviceId === preferred)) return preferred;
    return devices[0]?.deviceId || "";
  }

  private readTranscript(data: Uint8Array | ArrayBuffer, onTranscript: (line: TranscriptUpdate) => void) {
    if (!data) return;
    const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
    let frame: any;
    try {
      frame = SdkTranscriptFrame.decode(bytes);
    } catch {
      return;
    }
    for (const event of frame?.events || []) {
      if (event?.status) {
        if (Number(event.status.type) === 5) {
          onTranscript({
            resultId: "",
            partial: false,
            speakerId: "",
            text: "",
            startTimeMs: 0,
            failed: true,
            statusMessage: event.status.message || "Transcript stopped.",
          });
        }
        continue;
      }
      for (const result of event?.transcript?.results || []) {
        const alternative = result.alternatives?.[0];
        const text = (alternative?.transcript || "").trim();
        if (!text || !result.resultId) continue;
        const speakerId = alternative?.items?.find((item: any) => item?.speakerExternalUserId)?.speakerExternalUserId || "";
        onTranscript({
          resultId: String(result.resultId),
          partial: !!result.isPartial,
          speakerId,
          text,
          startTimeMs: this.asNumber(result.startTime),
        });
      }
    }
  }

  private asNumber(value: any): number {
    if (value == null) return 0;
    if (typeof value === "number") return value;
    if (typeof value.toNumber === "function") return value.toNumber();
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
